<script lang="ts" setup>
import type { CMSBlog, CMSMatchStoryPage } from '#shared/types/cmsLayout.ts'
import type { ExtendedBlogPostProps } from '~/types/elementsUi.ts'
import { heroImages, matchStoryImages } from '#shared/models/images.ts'
import { sampleSize } from 'es-toolkit'
import { useLivePreview } from '@payloadcms/live-preview-vue'
import { logoImages } from '~/models/images'
import type { BreadcrumbItem } from '@nuxt/ui'

definePageMeta({
  layout: false
})

interface CMSDataResponse {
  page: CMSMatchStoryPage
  relatedPages: CMSBlog<keyof typeof matchStoryImages>[]
}

const { t, te, baseKey } = useI18n('verhalen.slug')

const localeStore = useLocaleStore()
const locale = computed(() => localeStore.locale)

const route = useRoute('verhalen-slug')
const isPreview = computed(() => !!route.query.preview)

const { data: currentMatchStory, error: currentMatchStoryError } =
  await useAsyncData<CMSDataResponse>(
    `match-story/${route.params.slug}${isPreview.value ? `?preview=${route.query.preview}` : ''}`,
    () =>
      $fetch('/api/website-page/fetch-slug', {
        query: {
          slug: route.params.slug,
          type: 'match-story-pages',
          preview: route.query.preview ?? ''
        }
      })
  )

if (currentMatchStoryError.value) {
  throw createError({
    status: currentMatchStoryError.value.status,
    statusText: currentMatchStoryError.value.statusText
  })
}

const config = useRuntimeConfig()
let pageData: Ref<CMSMatchStoryPage> | undefined
if (isPreview.value && currentMatchStory.value) {
  const { data: tempData } = useLivePreview<CMSMatchStoryPage>({
    initialData: currentMatchStory.value.page,
    serverURL: config.public.cmsUrl
  })
  pageData = tempData
} else if (currentMatchStory.value) {
  pageData = toRef(currentMatchStory.value.page)
}

const initialData = computed<CMSDataResponse | undefined>(() => {
  if (!pageData?.value) {
    return undefined
  }

  return {
    page: pageData.value,
    relatedPages: currentMatchStory.value?.relatedPages ?? []
  }
})

const computedHero = computed(() => {
  if (!initialData.value) {
    return undefined
  }

  return {
    ...initialData.value.page.hero,
    image:
      initialData.value.page.hero.image in matchStoryImages
        ? matchStoryImages[initialData.value.page.hero.image]
        : heroImages['vincent-stopt-eten-in-bakje']
  }
})

const breadcrumbs = computed<BreadcrumbItem[]>(() => {
  if (!initialData.value) {
    return []
  }

  return [
    {
      to: '/verhalen',
      label: t('.breadcrumb-title')
    },
    {
      to: route.path,
      label: initialData.value.page.hero.title[locale.value]
    }
  ]
})

const sampledMatchStories = useState(`match-story-sample:${route.params.slug}`, () => {
  const matchStories = initialData.value?.relatedPages ?? []
  return sampleSize(matchStories, Math.min(matchStories.length, 3))
})

const relatedMatchStories = computed<ExtendedBlogPostProps[]>(() =>
  sampledMatchStories.value.map((story) => ({
    to: `/verhalen/${story.slug}`,
    title: story.title[locale.value],
    description: story.description[locale.value],
    date: story.datePublished,
    image:
      story.image in matchStoryImages
        ? matchStoryImages[story.image]
        : heroImages['vincent-stopt-eten-in-bakje']
  }))
)

const schemaOrg = computed(() => {
  if (!initialData.value) {
    return
  }

  const currentData = initialData.value.page

  const title = currentData.hero.title[locale.value]

  return getSchemaOrg(
    {
      title: title,
      breadcrumbs: [{ name: 'verhalen.index.title', item: '/verhalen' }, { name: title }],
      article: {
        description: currentData.hero.description[locale.value],
        image:
          currentData.hero.image in matchStoryImages
            ? matchStoryImages[currentData.hero.image].src.desktop
            : heroImages['vincent-stopt-eten-in-bakje'].src,
        datePublished: currentData.datePublished
      }
    },
    baseKey,
    { t, te }
  )
})
useSchemaOrg(schemaOrg)
</script>

<template>
  <div>
    <CMSStaticLayout
      v-if="initialData"
      type="match-story-pages"
      :data="initialData.page"
      :hero="computedHero"
      :breadcrumbs
    >
      <template v-if="initialData.page.hero.credits" #after-hero>
        <p class="mt-4 text-center font-bold">
          {{ t('.hero-taken-by', { name: initialData.page.hero.credits }) }}
        </p>
      </template>

      <template #prebuilt>
        <BlogPostsSection
          title=".related-match-stories-title"
          :blog-posts="relatedMatchStories"
        />

        <VSection class="flex flex-col items-center">
          <template #content>
            <p>{{ t('.all-stories-text') }}</p>
            <VButton label=".button" class="mt-6" :to="{ name: 'verhalen' }" />
          </template>
        </VSection>
      </template>
    </CMSStaticLayout>
    <CMSPreviewWidget v-if="isPreview" />
  </div>
</template>

<style scoped></style>
